import React, { useEffect } from 'react';
import { PublicLayout } from '@/components/public';
import { BRAND } from '@/config/brand';
import { SOLUTIONS_FOOTER_COLUMN } from '@/config/verticals';

import VerticalHero from './verticals/VerticalHero';
import AnswerFirstBox from './verticals/AnswerFirstBox';
import ValueBlock from './verticals/ValueBlock';
import VerticalFaq from './verticals/VerticalFaq';
import SpokeNav from './verticals/SpokeNav';
import InlineLink from './verticals/InlineLink';

const PAGE_TITLE =
  'Online Ordering & Delivery for Restaurants | Flat-Rate | Hungry Canadian';
const PAGE_DESCRIPTION =
  'Ditch 25%+ commission tax. Flat-rate online ordering, AI menu tools, and deep POS integration for full-service, quick-service, and multi-location Canadian restaurants.';

const NAV_PROPS = {
  links: [
    { href: '#pricing-value', label: 'Pricing value', testId: 'restaurants-nav-pricing-value' },
    { href: '#menu-ai', label: 'AI menu', testId: 'restaurants-nav-menu' },
    { href: '#pos-flows', label: 'POS & flows', testId: 'restaurants-nav-pos' },
    { href: '#customer-ownership', label: 'Customer chat', testId: 'restaurants-nav-customer' },
    { href: '#faq', label: 'FAQ', testId: 'restaurants-nav-faq' },
  ],
  primaryCta: { href: '/#lead-form-anchor', label: 'Get Started', testId: 'restaurants-nav-cta' },
};

const FOOTER_PROPS = {
  tagline:
    'A Canadian-owned delivery platform that keeps more of every sale with the local businesses that feed our communities.',
  columns: [
    {
      title: 'For Restaurants',
      links: [
        { href: '/', label: 'Why Hungry Canadian', testId: 'restaurants-footer-why' },
        { href: '/tools', label: 'Tools', testId: 'restaurants-footer-tools' },
        { href: '/features', label: 'Features', testId: 'restaurants-footer-features' },
        { href: '/integrations', label: 'Integrations', testId: 'restaurants-footer-integrations' },
      ],
    },
    SOLUTIONS_FOOTER_COLUMN,
    {
      title: 'Resources',
      links: [
        { href: 'https://help.hungrycanadian.com/', label: 'Help center', external: true, testId: 'restaurants-footer-help' },
        { href: `mailto:${BRAND.merchantsEmail}`, label: 'Contact merchants', external: true, testId: 'restaurants-footer-contact' },
      ],
    },
  ],
  legal: [
    { href: 'https://help.hungrycanadian.com/tos/', label: 'Terms of Service', external: true, testId: 'restaurants-footer-tos' },
    { href: 'https://help.hungrycanadian.com/privacypolicy/', label: 'Privacy Policy', external: true, testId: 'restaurants-footer-privacy' },
  ],
};

// Cross-link href constants (SSOT for this page)
const HREF_PRICING = '/#pricing';
const HREF_POS_LIGHTSPEED = '/integrations#pos-card-lightspeed';
const HREF_POS_SQUARE = '/integrations#pos-card-square';
const HREF_POS_CLOVER = '/integrations#pos-card-clover';
const HREF_POS_DELIVERECT = '/integrations#pos-card-deliverect';
const HREF_INTEGRATIONS_HUB = '/integrations';
// Cross-spoke sibling links
const HREF_CAFES_BAKERIES = '/for-cafes-bakeries';
const HREF_GHOST_KITCHENS = '/for-ghost-kitchens-popups';

// -----------------------------------------------------------------------------
// Restaurant-specific FAQ content
// - `a` may contain JSX (with inline links) → renders on-screen
// - `plainAnswer` is a plain string → used verbatim inside FAQPage JSON-LD
// -----------------------------------------------------------------------------
const RESTAURANT_FAQ = [
  {
    id: 'commission-vs-flat-rate',
    q: 'How much does Hungry Canadian cost compared to major delivery apps?',
    a: (
      <>
        Unlike traditional platforms that take an average of 25% commission on every order,
        Hungry Canadian offers predictable flat-rate pricing starting at{' '}
        <InlineLink href={HREF_PRICING} testId="faq-link-core-starter">
          $149/month for the Core Starter package
        </InlineLink>
        , allowing restaurants to keep 100% of their order revenue.
      </>
    ),
    plainAnswer:
      'Unlike traditional platforms that take an average of 25% commission on every order, Hungry Canadian offers predictable flat-rate pricing starting at $149/month for the Core Starter package, allowing restaurants to keep 100% of their order revenue.',
  },
  {
    id: 'pos-integration',
    q: 'Does Hungry Canadian integrate with my existing POS system?',
    a: (
      <>
        Yes. Hungry Canadian integrates with leading POS systems including{' '}
        <InlineLink href={HREF_POS_LIGHTSPEED} testId="faq-link-lightspeed">
          Lightspeed
        </InlineLink>
        ,{' '}
        <InlineLink href={HREF_POS_SQUARE} testId="faq-link-square">
          Square
        </InlineLink>
        ,{' '}
        <InlineLink href={HREF_POS_CLOVER} testId="faq-link-clover">
          Clover
        </InlineLink>
        , and{' '}
        <InlineLink href={HREF_POS_DELIVERECT} testId="faq-link-deliverect">
          Deliverect
        </InlineLink>
        , enabling automatic order injection and two-way menu/inventory syncing.
      </>
    ),
    plainAnswer:
      'Yes. Hungry Canadian integrates with leading POS systems including Lightspeed, Square, Clover, and Deliverect, enabling automatic order injection and two-way menu/inventory syncing.',
  },
];

// FAQPage JSON-LD source (plain-text answers only)
const FAQ_SCHEMA = RESTAURANT_FAQ.map(({ q, plainAnswer }) => ({ q, plainAnswer }));

// -----------------------------------------------------------------------------
// Value prop block content
// -----------------------------------------------------------------------------
const BLOCK_PRICING = {
  eyebrow: 'Predictable pricing',
  title: 'Eliminate Commission Taxes with Predictable Flat Pricing',
  blurb:
    'Traditional delivery apps frequently strip away up to 25%–30%+ on every single ticket—costing busy restaurants tens of thousands of dollars annually. Hungry Canadian flips this broken model.',
  bullets: [
    {
      label: 'Flat-Rate Predictability',
      detail: (
        <>
          Standard access begins at{' '}
          <InlineLink href={HREF_PRICING} testId="block-pricing-link-core">
            $149/month for the Core Starter package
          </InlineLink>
          , letting you keep your hard-earned profits.
        </>
      ),
    },
    {
      label: 'Unified Management',
      detail:
        'Accept pickup, direct delivery, and dine-in orders from a single, intuitive Web, Mobile, or Tablet dashboard.',
    },
    {
      label: 'Role-Based Permissions',
      detail:
        'Grant specific access controls to managers, kitchen staff, and front-of-house teams.',
    },
  ],
};

const BLOCK_MENU_AI = {
  eyebrow: 'Menu engineering',
  title: 'AI-Powered Menu Engineering & Complex Modifiers',
  blurb:
    'Turn a static paper menu into a fully mapped digital catalog in minutes — with the deep modifier logic a real kitchen actually needs.',
  bullets: [
    {
      label: 'AI Menu Builder',
      detail:
        'Upload PDF menus or menu photos to automatically construct mapped digital products and option groups within minutes. Bulk updates are equally fast via CSV import/export.',
    },
    {
      label: 'Nested Option Logic',
      detail:
        'Configure deep modifier trees (e.g., options within options, sub-ingredients, dietary swaps) with individual additive pricing and cost tracking.',
    },
    {
      label: 'Augmented Reality (AR) Previews',
      detail:
        'Let diners visually inspect 3D/AR models of dishes on their phones before placing an order.',
    },
    {
      label: 'Instant 86ing & Inventory Control',
      detail:
        'Out of an item? Toggle it offline instantly in your catalog manager to prevent costly remakes.',
    },
  ],
};

const BLOCK_POS = {
  eyebrow: 'Ecosystem & dispatch',
  title: 'POS Ecosystem & Multi-Flow Dispatch',
  blurb: (
    <>
      Say goodbye to &ldquo;tablet hell.&rdquo; Hungry Canadian connects directly with your
      existing restaurant tech stack — see the full{' '}
      <InlineLink href={HREF_INTEGRATIONS_HUB} testId="block-pos-link-hub">
        Integrations Hub
      </InlineLink>{' '}
      for compatibility details.
    </>
  ),
  bullets: [
    {
      heading: 'POS Integrations',
      items: [
        {
          label: 'Seamless order & inventory sync',
          detail: (
            <>
              Sync orders and inventory with{' '}
              <InlineLink href={HREF_POS_LIGHTSPEED} testId="block-pos-link-lightspeed">
                Lightspeed POS
              </InlineLink>
              ,{' '}
              <InlineLink href={HREF_POS_SQUARE} testId="block-pos-link-square">
                Square POS
              </InlineLink>
              ,{' '}
              <InlineLink href={HREF_POS_CLOVER} testId="block-pos-link-clover">
                Clover POS
              </InlineLink>
              , and{' '}
              <InlineLink href={HREF_POS_DELIVERECT} testId="block-pos-link-deliverect">
                Deliverect
              </InlineLink>
              .
            </>
          ),
        },
      ],
    },
    {
      heading: 'Flexible Order Flows',
      items: [
        {
          label: 'Route to whatever your kitchen actually uses',
          detail:
            'Support Tablet, Desktop, Auto-Accept, Auto-Printing, and direct POS routing based on your kitchen\u2019s exact operational speed.',
        },
      ],
    },
  ],
};

const BLOCK_OWNERSHIP = {
  eyebrow: 'Own the guest',
  title: 'Customer Ownership & Direct Real-Time Communication',
  blurb:
    'Third-party apps hide your customer data. Hungry Canadian hands it back — so you can run promos, resolve issues, and build loyalty on your own terms.',
  bullets: [
    {
      label: 'Order-Level Analytics',
      detail:
        'Identify your top-performing items, track profit margins, and launch self-run promotions (e.g., BOGO, Order Discounts, Free Gifts).',
    },
    {
      label: '3-Way Real-Time Chat',
      detail:
        'Message back and forth directly with customers or delivery drivers on active orders to resolve special requests or delivery notes instantly.',
    },
  ],
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function ForRestaurantsPage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    let meta = document.querySelector('meta[name="description"]');
    const prevDescription = meta?.getAttribute('content');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', PAGE_DESCRIPTION);

    return () => {
      document.title = prevTitle;
      if (prevDescription != null) meta.setAttribute('content', prevDescription);
    };
  }, []);

  return (
    <PublicLayout navProps={NAV_PROPS} footerProps={FOOTER_PROPS}>
      <div data-testid="for-restaurants-page">
        <VerticalHero
          eyebrow="For Restaurants"
          h1="Predictable Online Ordering & Delivery for Restaurants"
          tagline={
            <>
              Ditch the 25%+ commission tax. Power your full-service, quick-service, or{' '}
              <InlineLink href={HREF_GHOST_KITCHENS} testId="hero-tagline-link-multiloc">
                multi-location restaurant
              </InlineLink>{' '}
              with{' '}
              <InlineLink href={HREF_PRICING} testId="hero-tagline-link-flat-rate">
                flat-rate online ordering
              </InlineLink>
              , AI menu tools, and{' '}
              <InlineLink href={HREF_INTEGRATIONS_HUB} testId="hero-tagline-link-pos">
                deep POS integration
              </InlineLink>
              .
            </>
          }
          primaryCta={{
            href: '/#lead-form-anchor',
            label: 'Get Started',
            testId: 'restaurants-hero-primary-cta',
          }}
          secondaryCta={{
            href: HREF_PRICING,
            label: 'See Pricing',
            testId: 'restaurants-hero-secondary-cta',
          }}
        />

        <AnswerFirstBox
          title="How Hungry Canadian Empowers Restaurants"
          testId="restaurants-answer-first"
        >
          Hungry Canadian replaces high-commission delivery apps (which average 25% per order)
          with a predictable{' '}
          <InlineLink href={HREF_PRICING} testId="answer-link-pricing">
            $149/month flat-rate model
          </InlineLink>
          . Restaurants retain 100% of their margins while gaining access to an AI-assisted
          menu builder, nested customization modifiers,{' '}
          <InlineLink href={HREF_CAFES_BAKERIES} testId="answer-link-ar-previews">
            AR product previews
          </InlineLink>
          , and{' '}
          <InlineLink href={HREF_INTEGRATIONS_HUB} testId="answer-link-integrations">
            direct integrations
          </InlineLink>{' '}
          with POS platforms like{' '}
          <InlineLink href={HREF_POS_LIGHTSPEED} testId="answer-link-lightspeed">
            Lightspeed
          </InlineLink>
          ,{' '}
          <InlineLink href={HREF_POS_SQUARE} testId="answer-link-square">
            Square
          </InlineLink>
          ,{' '}
          <InlineLink href={HREF_POS_CLOVER} testId="answer-link-clover">
            Clover
          </InlineLink>
          , and{' '}
          <InlineLink href={HREF_POS_DELIVERECT} testId="answer-link-deliverect">
            Deliverect
          </InlineLink>
          .
        </AnswerFirstBox>

        <div id="pricing-value">
          <ValueBlock {...BLOCK_PRICING} testId="restaurants-block-pricing" />
        </div>
        <div id="menu-ai">
          <ValueBlock {...BLOCK_MENU_AI} testId="restaurants-block-menu-ai" reverse />
        </div>
        <div id="pos-flows">
          <ValueBlock {...BLOCK_POS} testId="restaurants-block-pos" />
        </div>
        <div id="customer-ownership">
          <ValueBlock {...BLOCK_OWNERSHIP} testId="restaurants-block-ownership" reverse />
        </div>

        <VerticalFaq
          items={RESTAURANT_FAQ}
          schema={FAQ_SCHEMA}
          testId="restaurants-faq"
          eyebrow="Restaurant FAQ"
          description="Straight answers to the questions restaurants ask before switching from commission-heavy delivery apps."
        />

        <SpokeNav currentSlug="for-restaurants" />
      </div>
    </PublicLayout>
  );
}
